import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

interface ArticleLayoutProps {
  title: string;
  subtitle?: string;
  heroImageUrl: string;
  heroImageAlt: string;
  heroImageTitle: string;
  schemaData?: Record<string, unknown>;
  children: React.ReactNode;
}

export default function ArticleLayout({
  title,
  subtitle,
  heroImageUrl,
  heroImageAlt,
  heroImageTitle,
  schemaData,
  children,
}: ArticleLayoutProps) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      {schemaData && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
        />
      )}
      <Navbar />
      <main>
        <article>
          <section className="relative w-full min-h-[300px] md:min-h-[380px] flex items-end overflow-hidden">
            <div className="absolute inset-0">
              <img
                src={heroImageUrl}
                alt={heroImageAlt}
                title={heroImageTitle}
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/20 to-black/20"></div>
            <div className="relative z-10 w-full max-w-4xl mx-auto px-4 md:px-6 pb-8 md:pb-10">
              <div className="flex items-center gap-2 text-white/70 text-xs mb-3 flex-wrap">
                <Link to="/" className="hover:text-white transition-colors whitespace-nowrap">Startseite</Link>
                <span className="w-3 h-3 flex items-center justify-center">
                  <i className="ri-arrow-right-s-line text-[10px]"></i>
                </span>
                <span className="text-white/50 whitespace-nowrap">OOPBUY Artikel</span>
              </div>
              <h1 className="text-xl md:text-3xl lg:text-4xl font-heading font-bold text-white leading-tight">
                {title}
              </h1>
              {subtitle && (
                <p className="text-sm md:text-base text-white/70 mt-3 max-w-2xl">{subtitle}</p>
              )}
            </div>
          </section>

          <section className="w-full py-10 md:py-14">
            <div className="w-full max-w-3xl mx-auto px-4 md:px-6">
              <div className="text-foreground-700">
                {children}

                <div className="mt-12 p-6 md:p-8 bg-background-100 rounded-xl border border-background-200/50">
                  <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-4">
                    Fazit
                  </h2>
                  <p className="text-sm md:text-base leading-relaxed mb-4 text-foreground-700">
                    Das <strong>OOPBUY Spreadsheet</strong> ist mehr als nur eine einfache Produktliste - es ist ein umfassendes Werkzeug, das den Einkaufsprozess für deutsche Käufer grundlegend vereinfacht. Mit seiner klaren Struktur, den regelmäßigen Aktualisierungen und der breiten Kategorieabdeckung bietet es einen echten Mehrwert im internationalen Online-Handel.
                  </p>
                  <p className="text-sm md:text-base leading-relaxed text-foreground-700">
                    Egal ob du ein erfahrener Online-Shopper oder ein kompletter Neueinsteiger bist - das OOPBUY Spreadsheet hilft dir, bessere und schnellere Kaufentscheidungen zu treffen.
                  </p>
                </div>

                <div className="mt-10 text-center">
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium bg-primary-500 text-background-50 rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-left-line"></i>
                    </span>
                    Zurück zur Startseite
                  </Link>
                </div>

                <div className="mt-8 text-center">
                  <p className="text-sm text-foreground-500 mb-3">
                    Möchtest du das OOPBUY Spreadsheet direkt nutzen?
                  </p>
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold bg-accent-500 text-background-50 rounded-lg hover:bg-accent-600 transition-colors whitespace-nowrap"
                  >
                    Jetzt OOPBUY Spreadsheet öffnen
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-external-link-line"></i>
                    </span>
                  </a>
                </div>
              </div>
            </div>
          </section>
        </article>
      </main>
      <Footer />
    </div>
  );
}